// Custom GLSL for the GPU-driven neural web (color-coded lobes) with optional
// coronal clipping-plane support and a high-intensity "Clutch" state.

export const particleVertex = /* glsl */ `
  uniform float uTime;
  uniform float uAnticip;
  uniform float uEvent;      // 0 none, 1 win, 2 loss, 3 clutch
  uniform float uEventTime;
  uniform float uHover;
  uniform float uSize;
  uniform float uPixelRatio;
  uniform float uWinBoost;

  uniform vec3 uFrontal;
  uniform vec3 uParietal;
  uniform vec3 uTemporal;
  uniform vec3 uOccipital;
  uniform vec3 uLimbic;
  uniform vec3 uGeneric;
  uniform vec3 uRed;
  uniform vec3 uGold;
  uniform vec3 uCrimson;

  attribute float aRegion;
  attribute float aSeed;

  varying vec3 vColor;
  varying float vGlow;

  #include <clipping_planes_pars_vertex>

  void main() {
    vec3 pos = position;
    float seed = aSeed;
    float region = aRegion;

    float hemi = pos.x < 0.0 ? 0.0 : 1.0;
    float breathe = sin(uTime * 1.1 + seed * 6.2831 + hemi * 1.7) * 0.5 + 0.5;
    pos += normalize(position + 0.0001) * (breathe * 0.012);

    vec3 baseCol = uGeneric;
    if (abs(region - 1.0) < 0.5) baseCol = uFrontal;
    else if (abs(region - 2.0) < 0.5) baseCol = uParietal;
    else if (abs(region - 3.0) < 0.5) baseCol = uTemporal;
    else if (abs(region - 4.0) < 0.5) baseCol = uOccipital;
    else if (region > 4.5) baseCol = uLimbic;

    vec3 col = baseCol;
    float isLobe = region < 0.5 ? 0.0 : 1.0;
    float glow = mix(0.22, 0.82, isLobe) + breathe * 0.18;
    float sizeMul = mix(0.5, 0.9, isLobe) + breathe * 0.22;

    float hov = (region > 0.5 && abs(uHover - region) < 0.5) ? 1.0 : 0.0;
    if (uHover > 8.5 && region > 4.5) hov = 1.0; // Neuro-Tour: highlight all limbic cores
    float hpulse = 0.5 + 0.5 * sin(uTime * 4.5);
    glow += hov * (0.7 + 0.8 * hpulse);
    sizeMul += hov * (0.4 + 0.4 * hpulse);

    if (abs(region - 2.0) < 0.5) {
      float amt = uAnticip;
      glow += amt * 0.95;
      float vib = sin(uTime * 38.0 + seed * 25.0);
      pos += vec3(vib, vib * 0.6, vib * 0.8) * amt * 0.02;
      sizeMul += amt * 0.7;
    }

    if (abs(region - 1.0) < 0.5) {
      float amt = uAnticip;
      glow += amt * 0.95;
      vec3 dir = normalize(position - vec3(0.0, 0.14, 0.36) + 0.0001);
      float crack = sin(seed * 33.0) * 0.5 + 0.5;
      pos += dir * amt * (0.04 + crack * 0.10);
      pos += vec3(sin(uTime * 22.0 + seed * 12.0), cos(uTime * 18.0 + seed * 8.0), sin(uTime * 26.0 + seed * 4.0)) * amt * crack * 0.02;
      col = mix(baseCol, uRed, amt * 0.55);
      sizeMul += amt * 0.5;
    }

    // Big Win
    if (uEvent > 0.5 && uEvent < 1.5 && abs(region - 4.0) < 0.5) {
      float t = clamp(uEventTime / 2.6, 0.0, 1.0);
      glow += (1.0 - t) * 0.7;
    }
    if (uEvent > 0.5 && uEvent < 1.5 && abs(region - 5.0) < 0.5) {
      float t = clamp(uEventTime / 2.6, 0.0, 1.0);
      col = uGold;
      vec3 dir = normalize(position - vec3(0.0, -0.10, 0.16) + 0.0001);
      pos += dir * pow(t, 0.6) * (2.0 + uWinBoost * 1.8);
      sizeMul += (1.0 - t) * (4.0 + uWinBoost * 3.2) + 1.2;
      glow = 1.3 + uWinBoost * 0.5;
    }

    // Loss
    if (uEvent > 1.5 && uEvent < 2.5 && abs(region - 6.0) < 0.5) {
      float t = clamp(uEventTime / 1.4, 0.0, 1.0);
      col = uCrimson;
      float shock = sin(t * 22.0) * (1.0 - t);
      vec3 dir = normalize(position - vec3(0.0, -0.15, 0.05) + 0.0001);
      pos += dir * shock * 0.5;
      sizeMul += (1.0 - t) * 2.5;
      glow = 0.9 + (1.0 - t) * 0.6;
    }

    // Clutch Moment: frontal rational failure (rapid red pulse) + limbic overload
    if (uEvent > 2.5) {
      float p = 0.5 + 0.5 * sin(uEventTime * 22.0);
      if (abs(region - 1.0) < 0.5) {
        col = mix(baseCol, uRed, 0.92);
        glow += 0.7 + p * 1.5;
        sizeMul += 0.5 + p * 0.8;
        pos += vec3(sin(uTime * 30.0 + seed * 9.0), cos(uTime * 28.0 + seed * 5.0), sin(uTime * 33.0 + seed * 3.0)) * 0.022 * p;
      }
      if (region > 4.5) {
        col = mix(baseCol, vec3(1.0), 0.4);
        glow += 1.9;
        sizeMul += 1.4;
      }
      if (abs(region - 2.0) < 0.5) {
        glow += 0.6 + p * 0.4;
      }
    }

    // Hemisphere Contrast: a Win floods the RIGHT hemisphere in gold, a Loss
    // floods the LEFT hemisphere in crimson — dramatising the reward/threat split.
    float rightH = step(0.0, position.x);
    if (uEvent > 0.5 && uEvent < 1.5) {
      float tw = clamp(uEventTime / 2.6, 0.0, 1.0);
      float wash = rightH * (1.0 - tw) * 0.9;
      col = mix(col, uGold, wash);
      glow += wash * 1.3;
      sizeMul += wash * 0.9;
    }
    if (uEvent > 1.5 && uEvent < 2.5) {
      float tl = clamp(uEventTime / 1.4, 0.0, 1.0);
      float wash = (1.0 - rightH) * (1.0 - tl) * 0.9;
      col = mix(col, uCrimson, wash);
      glow += wash * 1.1;
      sizeMul += wash * 0.7;
    }

    vColor = col;
    vGlow = glow;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    #include <clipping_planes_vertex>
    float ps = uSize * sizeMul * uPixelRatio / max(0.1, -mvPosition.z);
    gl_PointSize = clamp(ps, 1.0, 120.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

export const particleFragment = /* glsl */ `
  precision highp float;
  #include <clipping_planes_pars_fragment>
  varying vec3 vColor;
  varying float vGlow;

  void main() {
    #include <clipping_planes_fragment>
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.0, d);
    a = pow(a, 1.3);
    vec3 col = vColor * vGlow;
    col += vColor * smoothstep(0.14, 0.0, d) * 0.35;
    gl_FragColor = vec4(col, a);
  }
`;

export const brainVertex = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocal;
  #include <clipping_planes_pars_vertex>
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mvPosition.xyz);
    vLocal = position;
    #include <clipping_planes_vertex>
    gl_Position = projectionMatrix * mvPosition;
  }
`;

export const brainFragment = /* glsl */ `
  precision highp float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uMatte;
  uniform float uFlash;
  uniform vec3 uFlashColor;
  uniform vec3 uGlow;         // fluorescent emissive colour
  uniform float uGlowStrength;
  uniform float uFlashSide;    // 0 both, 1 right hemisphere, -1 left hemisphere
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocal;
  #include <clipping_planes_pars_fragment>
  void main() {
    #include <clipping_planes_fragment>
    float fres = pow(1.0 - max(0.0, dot(vNormal, vView)), 2.5);
    vec3 col = mix(uColor * 0.12, uColor, fres);
    col = mix(col, mix(uColor * 0.75, uColor, fres * 0.4), uMatte); // matte porcelain
    // fluorescent emissive rim glow (Top View cyan)
    col += uGlow * uGlowStrength * (0.35 + fres * 1.8);
    // Hemisphere-aware flash: Win tints the right half, Loss tints the left half.
    float sideMask = uFlashSide == 0.0 ? 1.0 : (uFlashSide > 0.0 ? step(0.0, vLocal.x) : step(vLocal.x, 0.0));
    col = mix(col, uFlashColor, uFlash * sideMask);
    // Longitudinal fissure: darken a thin band down the anatomical midline so the
    // left/right hemispheres read clearly (esp. from the Top View).
    float fis = smoothstep(0.058, 0.006, abs(vLocal.x));
    col *= 1.0 - fis * 0.72;
    float alpha = clamp(uOpacity + fres * 0.55 * (1.0 - uMatte * 0.7) + uFlash * 0.4 + uGlowStrength * (0.12 + fres * 0.5), 0.0, 0.98);
    alpha *= 1.0 - fis * 0.4;
    gl_FragColor = vec4(col, alpha);
  }
`;
